import { orderQueries } from '@/api/order-queries';
import { membershipOrderPreviewCounts } from '@/components/membership/membership-order-preview-mock';
import type { CplatApiResponse } from '@/types/api';
import type { OrderItem, OrderList, OrderSeachField } from '@/types/order-list';
import type { CarrierState } from '@/types/shipping';
import type { MarketStore, Stores } from '@/types/stores';
import type { ShippingCompany } from '@/types/user-info';

export type MembershipOrderDetailsPreviewStatusTab =
  | 'NewOrder'
  | 'ShippingReady'
  | 'Shipping'
  | 'ShippingCompleted'
  | 'CancelRequest'
  | 'ExchangeRequest'
  | 'ReturnRequest';

type MembershipOrderDetailsPreviewOrderStatusTab = Extract<
  MembershipOrderDetailsPreviewStatusTab,
  'NewOrder' | 'ShippingReady' | 'Shipping' | 'ShippingCompleted'
>;

type MembershipOrderDetailsPreviewOrderChangeStatusTab = Extract<
  MembershipOrderDetailsPreviewStatusTab,
  'CancelRequest' | 'ExchangeRequest' | 'ReturnRequest'
>;

type MembershipOrderDetailsPreviewSortInfo = {
  sortColumn: 'OrderDate' | 'SalesAmount' | 'OrderCount';
  sortDirection: 'asc' | 'desc';
};

export type MembershipOrderDetailsPreviewFilters = {
  dateFrom?: string;
  dateTo?: string;
  skip?: number;
  take?: number;
  storeId?: string;
  marketId?: number;
  searchField?: OrderSeachField;
  searchKeyword?: string;
  isOrderFurtherActionEnable?: boolean;
  sortInfo?: MembershipOrderDetailsPreviewSortInfo;
};

const MARKET_ICON_URLS = {
  smartstore:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketSmartstore.png',
  coupang:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketCoupang.png',
  gmarket:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarketGmarket.png',
  leader:
    'https://cplat-images.s3.ap-northeast-2.amazonaws.com/sellerbox/market-icon/IconMarket11st.png',
} as const;

const counts = membershipOrderPreviewCounts;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const PREVIEW_SOURCE_START_DATE_KEY = '2025-04-21';

const padTwo = (value: number) => value.toString().padStart(2, '0');

const formatDateKey = (date: Date) =>
  `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;

const parseDateKey = (dateKey: string) => {
  const [year = 0, month = 1, day = 1] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(date.getDate() + days);
  return nextDate;
};

const getDateDistanceDays = (startDateKey: string, endDateKey: string) =>
  Math.round(
    (parseDateKey(endDateKey).getTime() - parseDateKey(startDateKey).getTime()) / MS_PER_DAY,
  );

const getPositiveModulo = (value: number, divisor: number) =>
  ((value % divisor) + divisor) % divisor;

const createPreviewEndDate = () => {
  const endDate = new Date();
  endDate.setHours(0, 0, 0, 0);
  return endDate;
};

const createPreviewStartDate = (endDate = createPreviewEndDate()) => addDays(endDate, -6);

const previewEndDate = createPreviewEndDate();
const previewStartDate = createPreviewStartDate(previewEndDate);
const previewEndDateKey = formatDateKey(previewEndDate);

const createPreviewDateWindow = (filters?: MembershipOrderDetailsPreviewFilters) => {
  const fallbackStartDateKey = formatDateKey(previewStartDate);
  const fallbackEndDateKey = formatDateKey(previewEndDate);
  const filterStartDateKey = filters?.dateFrom?.slice(0, 10) ?? fallbackStartDateKey;
  const filterEndDateKey = filters?.dateTo?.slice(0, 10) ?? fallbackEndDateKey;
  const shouldSwap = parseDateKey(filterStartDateKey) > parseDateKey(filterEndDateKey);
  const startDateKey = shouldSwap ? filterEndDateKey : filterStartDateKey;
  const endDateKey = shouldSwap ? filterStartDateKey : filterEndDateKey;

  return {
    startDateKey,
    endDateKey,
    rangeLengthDays: Math.max(1, getDateDistanceDays(startDateKey, endDateKey) + 1),
  };
};

const mapPreviewOrderDateToWindow = (
  orderDate: string,
  dateWindow: ReturnType<typeof createPreviewDateWindow>,
) => {
  const sourceDateKey = orderDate.slice(0, 10);
  const sourceOffset = getDateDistanceDays(PREVIEW_SOURCE_START_DATE_KEY, sourceDateKey);
  const windowOffset = getPositiveModulo(sourceOffset, dateWindow.rangeLengthDays);
  const mappedDate = addDays(parseDateKey(dateWindow.startDateKey), windowOffset);

  return `${formatDateKey(mappedDate)}T${orderDate.slice(11, 19)}+09:00`;
};

const remapPreviewOrderDates = (
  orders: OrderItem[],
  filters?: MembershipOrderDetailsPreviewFilters,
) => {
  const dateWindow = createPreviewDateWindow(filters);

  return orders.map((order) => ({
    ...order,
    orderDate: mapPreviewOrderDateToWindow(order.orderDate, dateWindow),
  }));
};

const marketList: MarketStore[] = [
  {
    marketId: 1,
    storeId: 'sellerbox-store',
    marketName: '스마트스토어',
    marketNameEnglish: 'smartstore',
    marketIconUrl: MARKET_ICON_URLS.smartstore,
    hasPendingAction: true,
    marketAlias: '셀러박스',
    lastOrderDate: `${previewEndDateKey}T14:53:00+09:00`,
    marketSeqNo: 1,
  },
  {
    marketId: 2,
    storeId: 'sellerbox-direct',
    marketName: '셀러박스',
    marketNameEnglish: 'sellerbox',
    marketIconUrl: MARKET_ICON_URLS.coupang,
    hasPendingAction: false,
    marketAlias: 'sellerbox',
    lastOrderDate: `${previewEndDateKey}T14:20:00+09:00`,
    marketSeqNo: 2,
  },
  {
    marketId: 3,
    storeId: 'leader-store',
    marketName: '11번가',
    marketNameEnglish: '11st',
    marketIconUrl: MARKET_ICON_URLS.leader,
    hasPendingAction: false,
    marketAlias: 'Leader',
    lastOrderDate: `${previewEndDateKey}T12:32:00+09:00`,
    marketSeqNo: 3,
  },
  {
    marketId: 4,
    storeId: 'active-market',
    marketName: 'G마켓',
    marketNameEnglish: 'gmarket',
    marketIconUrl: MARKET_ICON_URLS.gmarket,
    hasPendingAction: false,
    marketAlias: '활기찬마켓',
    lastOrderDate: `${previewEndDateKey}T11:42:00+09:00`,
    marketSeqNo: 4,
  },
];

const registeredStores: Stores[] = marketList.map((market) => ({
  sellerId: 5942,
  marketId: market.marketId,
  storeId: market.storeId,
  interlockDate: `${previewEndDateKey}T00:00:00+09:00`,
  useYn: 'Y',
  loginValidYn: 'Y',
  marketAlias: market.marketAlias,
  marketName: market.marketName,
  marketNameEnglish: market.marketNameEnglish,
  marketIconUrl: market.marketIconUrl,
  sellerAdminPageUrl: 'https://app.sellerbox.io',
  sellerRefundPageUrl: 'https://app.sellerbox.io',
  coupangWingId: '',
}));

const createOrder = ({
  orderNo,
  market,
  orderDate,
  productName,
  productOption,
  salesAmount,
  orderCount,
  externalProductInfo,
  receiverName,
  receiverPhoneNo,
  receiverAddress,
  shippingMemo,
  ordererName,
  orderStatus = '신규주문',
  isOrderFurtherActionEnable = true,
}: {
  orderNo: string;
  market: MarketStore;
  orderDate: string;
  productName: string;
  productOption: string;
  salesAmount: number;
  orderCount: number;
  externalProductInfo: string;
  receiverName: string;
  receiverPhoneNo: string;
  receiverAddress: string;
  shippingMemo: string;
  ordererName: string;
  orderStatus?: string;
  isOrderFurtherActionEnable?: boolean;
}): OrderItem => {
  const ordererInfo = {
    ordererId: `${orderNo}-buyer`,
    ordererName,
    ordererPhoneNo: receiverPhoneNo,
  };
  const receiverInfo = {
    receiverName,
    receiverAddress,
    receiverZipCode: '00000',
    receiverPhoneNo,
    shippingMemo,
  };

  return {
    sellerId: 5942,
    marketId: market.marketId,
    storeId: market.storeId,
    marketAlias: market.marketAlias,
    orderNo,
    orderItemNo: `${orderNo}-1`,
    productNo: externalProductInfo,
    productName,
    productOption,
    orderDate,
    orderStatus,
    salesAmount,
    orderCount,
    ordererInfoJson: JSON.stringify(ordererInfo),
    receiverInfoJson: JSON.stringify(receiverInfo),
    ordererInfo,
    receiverInfo,
    externalProductInfo,
    coupangShipmentBoxNo: '',
    claimNo: '',
    tmallDeliveryNo: '',
    cancelCount: 0,
    marketInfo: {
      marketId: market.marketId,
      marketName: market.marketName,
      marketNameEnglish: market.marketNameEnglish,
      marketIconUrl: market.marketIconUrl,
      marketIconFulfillmentUrl: '',
      isOrderFurtherActionEnable,
      sellerAdminPageUrl: 'https://app.sellerbox.io',
      sellerRefundPageUrl: 'https://app.sellerbox.io',
    },
  };
};

const orders = [
  createOrder({
    orderNo: '20241028154731',
    market: marketList[0],
    orderDate: '2025-04-21T14:50:00+09:00',
    productName: '프리미엄 무선 이어폰 X9 Pro',
    productOption: '블랙',
    salesAmount: 50_000,
    orderCount: 1,
    externalProductInfo: 'SB-EAR-X9',
    receiverName: '김민준',
    receiverPhoneNo: '010-2847-3951',
    receiverAddress: '서울시 강남구 테헤란로 123 부재시 문앞',
    shippingMemo: '부재시 문앞',
    ordererName: '김민준',
  }),
  createOrder({
    orderNo: '20241029102384',
    market: marketList[0],
    orderDate: '2025-04-22T09:23:00+09:00',
    productName: '스마트 LED 스탠드 조명 S3',
    productOption: '화이트 / 2개입',
    salesAmount: 6_230,
    orderCount: 2,
    externalProductInfo: 'SB-LMP-S3',
    receiverName: '이서연',
    receiverPhoneNo: '010-3948-2847',
    receiverAddress: '경기도 성남시 분당구 판교로 456',
    shippingMemo: '배송 전 연락 주세요',
    ordererName: '이서연',
  }),
  createOrder({
    orderNo: '20241030143820',
    market: marketList[1],
    orderDate: '2025-04-23T11:47:00+09:00',
    productName: '레이저 알루미늄 노트북 거치대',
    productOption: '실버 M사이즈',
    salesAmount: 31_800,
    orderCount: 1,
    externalProductInfo: 'SB-STD-NB',
    receiverName: '박지훈',
    receiverPhoneNo: '010-4857-1938',
    receiverAddress: '판매자 페이지에서 확인',
    shippingMemo: '부재시 경비실',
    ordererName: '박지훈',
  }),
  createOrder({
    orderNo: '20241031120953',
    market: marketList[0],
    orderDate: '2025-04-24T16:32:00+09:00',
    productName: '고속충전 USB-C 케이블 2m',
    productOption: '화이트',
    salesAmount: 27_500,
    orderCount: 3,
    externalProductInfo: 'SB-CBL-TC',
    receiverName: '박지훈',
    receiverPhoneNo: '010-4857-1938',
    receiverAddress: '서울시 마포구 월드컵북로 789 경비실',
    shippingMemo: '경비실 보관',
    ordererName: '박지훈',
  }),
  createOrder({
    orderNo: '20241101155348',
    market: marketList[0],
    orderDate: '2025-04-25T13:15:00+09:00',
    productName: '블루투스 5.0 포터블 스피커',
    productOption: '네이비',
    salesAmount: 19_200,
    orderCount: 1,
    externalProductInfo: 'SB-SPK-BT',
    receiverName: '최수아',
    receiverPhoneNo: '010-5948-3847',
    receiverAddress: '인천시 남동구 아암대로 321 부재시 문앞',
    shippingMemo: '문앞 요청',
    ordererName: '최수아',
  }),
  createOrder({
    orderNo: '20241102104729',
    market: marketList[0],
    orderDate: '2025-04-26T10:08:00+09:00',
    productName: '아이폰15 Pro 강화유리 필름',
    productOption: '아이폰15 Pro용',
    salesAmount: 13_400,
    orderCount: 2,
    externalProductInfo: 'SB-FLM-IP15',
    receiverName: '정민호',
    receiverPhoneNo: '010-6847-2938',
    receiverAddress: '서울시 서초구 서초대로 654',
    shippingMemo: '빠른 배송 부탁드립니다',
    ordererName: '정민호',
  }),
  createOrder({
    orderNo: '20241103162840',
    market: marketList[1],
    orderDate: '2025-04-27T15:44:00+09:00',
    productName: '무소음 무선 옵티컬 마우스',
    productOption: '블랙 / 무소음',
    salesAmount: 21_800,
    orderCount: 1,
    externalProductInfo: 'SB-MOU-WL',
    receiverName: '김도윤',
    receiverPhoneNo: '010-8234-5678',
    receiverAddress: '판매자 페이지에서 확인',
    shippingMemo: '부재시 문앞',
    ordererName: '김도윤',
  }),
  createOrder({
    orderNo: '20241104120398',
    market: marketList[0],
    orderDate: '2025-04-28T08:56:00+09:00',
    productName: '메모리폼 키보드 손목 받침대',
    productOption: '베이지 / 대형',
    salesAmount: 9_800,
    orderCount: 1,
    externalProductInfo: 'SB-RST-MF',
    receiverName: '홍길동',
    receiverPhoneNo: '010-1234-5678',
    receiverAddress: '경기도 수원시 영통구 광교호수로 111',
    shippingMemo: '문앞에 놓아주세요',
    ordererName: '홍길동',
  }),
  createOrder({
    orderNo: '20241105143572',
    market: marketList[0],
    orderDate: '2025-04-29T14:21:00+09:00',
    productName: '4구 멀티탭 USB 2포트 1.5m',
    productOption: '화이트 1.5m',
    salesAmount: 8_500,
    orderCount: 2,
    externalProductInfo: 'SB-PWR-4P',
    receiverName: '김소희',
    receiverPhoneNo: '010-7384-2948',
    receiverAddress: '부산시 해운대구 센텀동로 222',
    shippingMemo: '경비실 맡겨주세요',
    ordererName: '김소희',
  }),
  createOrder({
    orderNo: '20241106182390',
    market: marketList[0],
    orderDate: '2025-04-30T17:33:00+09:00',
    productName: '알루미늄 태블릿 거치대 PRO',
    productOption: '스페이스그레이',
    salesAmount: 16_700,
    orderCount: 1,
    externalProductInfo: 'SB-STD-TB',
    receiverName: '이준서',
    receiverPhoneNo: '010-8473-9284',
    receiverAddress: '서울시 송파구 올림픽로 333 부재시 문앞',
    shippingMemo: '부재시 문앞',
    ordererName: '이준서',
  }),
];

const shippingCompanyList: ShippingCompany[] = [
  { shippingCompanyCode: 'CJGLS', shippingCompanyName: 'CJ대한통운', isPrefer: true },
  { shippingCompanyCode: 'HANJIN', shippingCompanyName: '한진택배', isPrefer: true },
  { shippingCompanyCode: 'LOGEN', shippingCompanyName: '로젠택배', isPrefer: false },
  { shippingCompanyCode: 'EPOST', shippingCompanyName: '우체국택배', isPrefer: false },
  { shippingCompanyCode: 'LOTTE', shippingCompanyName: '롯데택배', isPrefer: false },
];

const getShippingCompany = (shippingCompanyName: string) => {
  const shippingCompany = shippingCompanyList.find(
    (company) => company.shippingCompanyName === shippingCompanyName,
  );

  if (!shippingCompany) {
    throw new Error(`Unknown preview shipping company: ${shippingCompanyName}`);
  }

  return shippingCompany;
};

const readyToShipOrders = [
  createOrder({
    orderNo: '20241028154731',
    market: marketList[0],
    orderDate: '2025-04-21T14:50:00+09:00',
    productName: '프리미엄 무선 이어폰 X9 Pro',
    productOption: '블랙',
    salesAmount: 78_200,
    orderCount: 1,
    externalProductInfo: 'SB-EAR-X9-',
    receiverName: '박하은',
    receiverPhoneNo: '010-9384-7284',
    receiverAddress: '대구시 수성구 달구벌대로 444',
    shippingMemo: '부재시 문앞',
    ordererName: '박하은',
    orderStatus: '발송대기',
  }),
  createOrder({
    orderNo: '20241029102384',
    market: marketList[0],
    orderDate: '2025-04-22T09:23:00+09:00',
    productName: '스마트 LED 스탠드 조명 S3',
    productOption: '화이트 / 2개입',
    salesAmount: 59_400,
    orderCount: 1,
    externalProductInfo: 'SB-LMP-S3',
    receiverName: '최도현',
    receiverPhoneNo: '010-3847-2918',
    receiverAddress: '경기도 고양시 일산동구 중앙로 555',
    shippingMemo: '배송 전 연락 주세요',
    ordererName: '최도현',
    orderStatus: '발송대기',
  }),
  createOrder({
    orderNo: '20241030143820',
    market: marketList[0],
    orderDate: '2025-04-23T11:47:00+09:00',
    productName: '접이식 알루미늄 노트북 거치대',
    productOption: '실버 M사이즈',
    salesAmount: 42_100,
    orderCount: 1,
    externalProductInfo: 'SB-STD-NB',
    receiverName: '김민준',
    receiverPhoneNo: '010-2847-3951',
    receiverAddress: '서울시 강남구 테헤란로 123 부재시 문앞',
    shippingMemo: '문앞 배송 요청',
    ordererName: '김민준',
    orderStatus: '발송대기',
  }),
  createOrder({
    orderNo: '20241031120953',
    market: marketList[1],
    orderDate: '2025-04-24T16:32:00+09:00',
    productName: '고속충전 USB-C 케이블 2m',
    productOption: '화이트',
    salesAmount: 30_500,
    orderCount: 1,
    externalProductInfo: 'SB-CBL-TC',
    receiverName: '',
    receiverPhoneNo: '',
    receiverAddress: '',
    shippingMemo: '',
    ordererName: '이서연',
    orderStatus: '발송대기',
    isOrderFurtherActionEnable: false,
  }),
  createOrder({
    orderNo: '20241101153482',
    market: marketList[0],
    orderDate: '2025-04-25T13:15:00+09:00',
    productName: '블루투스 5.0 포터블 스피커',
    productOption: '네이비',
    salesAmount: 21_800,
    orderCount: 2,
    externalProductInfo: 'SB-SPK-BT',
    receiverName: '이서연',
    receiverPhoneNo: '010-3948-2847',
    receiverAddress: '경기도 성남시 분당구 판교로 456',
    shippingMemo: '경비실 맡겨주세요',
    ordererName: '박지훈',
    orderStatus: '발송대기',
  }),
  createOrder({
    orderNo: '20241102104729',
    market: marketList[0],
    orderDate: '2025-04-26T10:08:00+09:00',
    productName: '아이폰15 Pro 강화유리 필름',
    productOption: '아이폰15 Pro용',
    salesAmount: 15_600,
    orderCount: 1,
    externalProductInfo: 'SB-FLM-IP15',
    receiverName: '박지훈',
    receiverPhoneNo: '010-4857-1938',
    receiverAddress: '서울시 마포구 월드컵북로 789 경비실',
    shippingMemo: '빠른 배송 부탁드립니다',
    ordererName: '최수아',
    orderStatus: '발송대기',
  }),
  createOrder({
    orderNo: '20241103162840',
    market: marketList[0],
    orderDate: '2025-04-27T15:44:00+09:00',
    productName: '무소음 무선 옵티컬 마우스',
    productOption: '블랙 / 무소음',
    salesAmount: 18_900,
    orderCount: 1,
    externalProductInfo: 'SB-MOU-WL',
    receiverName: '최수아',
    receiverPhoneNo: '010-5948-3847',
    receiverAddress: '인천시 남동구 인하로 321 부재시 문앞',
    shippingMemo: '부재시 문앞',
    ordererName: '정민호',
    orderStatus: '발송대기',
  }),
  createOrder({
    orderNo: '20241104120938',
    market: marketList[0],
    orderDate: '2025-04-28T08:56:00+09:00',
    productName: '메모리폼 키보드 손목 받침대',
    productOption: '베이지 / 대형',
    salesAmount: 12_300,
    orderCount: 1,
    externalProductInfo: 'SB-RST-MF',
    receiverName: '정민호',
    receiverPhoneNo: '010-6847-2938',
    receiverAddress: '서울시 서초구 서초대로 654',
    shippingMemo: '문앞에 놓아주세요',
    ordererName: '홍길동',
    orderStatus: '발송대기',
  }),
  createOrder({
    orderNo: '20241105143702',
    market: marketList[0],
    orderDate: '2025-04-29T14:21:00+09:00',
    productName: '4구 멀티탭 USB 2포트 1.5m',
    productOption: '화이트 1.5m',
    salesAmount: 9_200,
    orderCount: 1,
    externalProductInfo: 'SB-PWR-4P',
    receiverName: '홍길동',
    receiverPhoneNo: '010-1234-5678',
    receiverAddress: '경기도 수원시 영통구 광교중앙로 111',
    shippingMemo: '부재시 경비실',
    ordererName: '김소희',
    orderStatus: '발송대기',
  }),
  createOrder({
    orderNo: '20241106182390',
    market: marketList[3],
    orderDate: '2025-04-30T17:33:00+09:00',
    productName: '알루미늄 태블릿 거치대 PRO',
    productOption: '스페이스그레이',
    salesAmount: 23_400,
    orderCount: 2,
    externalProductInfo: 'SB-STD-TB',
    receiverName: '김소희',
    receiverPhoneNo: '010-7384-2948',
    receiverAddress: '부산시 해운대구 센텀동로 222',
    shippingMemo: '경비실 맡겨주세요',
    ordererName: '이준서',
    orderStatus: '발송대기',
  }),
];

const getReadyToShipOrder = (orderNo: string) => {
  const order = readyToShipOrders.find((item) => item.orderNo === orderNo);

  if (!order) {
    throw new Error(`Unknown preview ready-to-ship order: ${orderNo}`);
  }

  return order;
};

const createReadyToShipCarrierState = (
  orderNo: string,
  shippingCompanyName: string,
  trackingNumber: string,
): CarrierState => ({
  target: getReadyToShipOrder(orderNo),
  carrier: getShippingCompany(shippingCompanyName),
  trackingNumber,
});

const readyToShipCarrierState: CarrierState[] = [
  createReadyToShipCarrierState('20241028154731', 'CJ대한통운', '621028947963'),
  createReadyToShipCarrierState('20241029102384', '한진택배', '501938472910'),
  createReadyToShipCarrierState('20241030143820', '로젠택배', '632847561023'),
  createReadyToShipCarrierState('20241101153482', '우체국택배', '6219388571024'),
  createReadyToShipCarrierState('20241102104729', '롯데택배', '501847362910'),
  createReadyToShipCarrierState('20241103162840', 'CJ대한통운', '6320384471025'),
  createReadyToShipCarrierState('20241104120938', '한진택배', '621847293010'),
  createReadyToShipCarrierState('20241105143702', '우체국택배', '5019382864715'),
  createReadyToShipCarrierState('20241106182390', '롯데택배', '632748291036'),
];

const shippingOrders = [
  createOrder({
    orderNo: '20241028154',
    market: marketList[0],
    orderDate: '2025-04-21T14:50:00+09:00',
    productName: '프리미엄 무선 이어폰 X9 Pro',
    productOption: '블랙',
    salesAmount: 25_340,
    orderCount: 1,
    externalProductInfo: 'SB-EAR-X9',
    receiverName: '이준서',
    receiverPhoneNo: '010-8473-9284',
    receiverAddress: '서울시 송파구 올림픽로 333 부재시 문앞',
    shippingMemo: '문앞 배송',
    ordererName: '이준서',
    orderStatus: '배송중',
  }),
  createOrder({
    orderNo: '20241029102',
    market: marketList[0],
    orderDate: '2025-04-22T09:23:00+09:00',
    productName: '스마트 LED 스탠드 조명 S3',
    productOption: '화이트 / 2개입',
    salesAmount: 18_920,
    orderCount: 2,
    externalProductInfo: 'SB-LMP-S3',
    receiverName: '박하은',
    receiverPhoneNo: '010-9384-7284',
    receiverAddress: '대구시 수성구 달구벌대로 444',
    shippingMemo: '배송 전 연락 주세요',
    ordererName: '박하은',
    orderStatus: '배송중',
  }),
  createOrder({
    orderNo: '20241030143',
    market: marketList[2],
    orderDate: '2025-04-23T11:47:00+09:00',
    productName: '접이식 알루미늄 노트북 거치대',
    productOption: '실버 M사이즈',
    salesAmount: 21_650,
    orderCount: 1,
    externalProductInfo: 'SB-STD-NB',
    receiverName: '',
    receiverPhoneNo: '',
    receiverAddress: '',
    shippingMemo: '',
    ordererName: '김민준',
    orderStatus: '배송중',
  }),
  createOrder({
    orderNo: '20241031120',
    market: marketList[0],
    orderDate: '2025-04-24T16:32:00+09:00',
    productName: '고속충전 USB-C 케이블 2m',
    productOption: '화이트',
    salesAmount: 23_110,
    orderCount: 3,
    externalProductInfo: 'SB-CBL-TC',
    receiverName: '최도현',
    receiverPhoneNo: '010-3847-2918',
    receiverAddress: '경기도 고양시 일산동구 중앙로 555',
    shippingMemo: '부재시 문앞',
    ordererName: '이서연',
    orderStatus: '배송중',
  }),
];

const deliveredOrders = [
  createOrder({
    orderNo: '20241101153',
    market: marketList[0],
    orderDate: '2025-04-25T13:15:00+09:00',
    productName: '블루투스 5.0 포터블 스피커',
    productOption: '네이비',
    salesAmount: 9_640,
    orderCount: 1,
    externalProductInfo: 'SB-SPK-BT',
    receiverName: '김민준',
    receiverPhoneNo: '010-2847-3951',
    receiverAddress: '서울시 강남구 테헤란로 123',
    shippingMemo: '-',
    ordererName: '김민준',
    orderStatus: '구매확정',
  }),
  createOrder({
    orderNo: '20241102104',
    market: marketList[0],
    orderDate: '2025-04-26T10:08:00+09:00',
    productName: '아이폰15 Pro 강화유리 필름',
    productOption: '아이폰15 Pro용',
    salesAmount: 7_310,
    orderCount: 2,
    externalProductInfo: 'SB-FLM-IP15',
    receiverName: '이서연',
    receiverPhoneNo: '010-3948-2847',
    receiverAddress: '경기도 성남시 분당구 판교로 456',
    shippingMemo: '-',
    ordererName: '이서연',
    orderStatus: '구매확정',
  }),
  createOrder({
    orderNo: '20241103162',
    market: marketList[2],
    orderDate: '2025-04-27T15:44:00+09:00',
    productName: '무소음 무선 옵티컬 마우스',
    productOption: '블랙 / 무소음',
    salesAmount: 25_820,
    orderCount: 1,
    externalProductInfo: 'SB-MOU-WL',
    receiverName: '',
    receiverPhoneNo: '',
    receiverAddress: '',
    shippingMemo: '',
    ordererName: '박지훈',
    orderStatus: '배송완료',
  }),
  createOrder({
    orderNo: '20241104120',
    market: marketList[0],
    orderDate: '2025-04-28T08:56:00+09:00',
    productName: '메모리폼 키보드 손목 받침대',
    productOption: '베이지 / 대형',
    salesAmount: 14_390,
    orderCount: 1,
    externalProductInfo: 'SB-RST-MF',
    receiverName: '박지훈',
    receiverPhoneNo: '010-4857-1938',
    receiverAddress: '서울시 마포구 월드컵북로 789',
    shippingMemo: '-',
    ordererName: '박지훈',
    orderStatus: '구매확정',
  }),
  createOrder({
    orderNo: '20241105143',
    market: marketList[0],
    orderDate: '2025-04-29T14:21:00+09:00',
    productName: '4구 멀티탭 USB 2포트 1.5m',
    productOption: '화이트 1.5m',
    salesAmount: 13_150,
    orderCount: 2,
    externalProductInfo: 'SB-PWR-4P',
    receiverName: '최수아',
    receiverPhoneNo: '010-5948-3847',
    receiverAddress: '인천시 남동구 인하로 321',
    shippingMemo: '-',
    ordererName: '최수아',
    orderStatus: '구매확정',
  }),
  createOrder({
    orderNo: '20241106182',
    market: marketList[0],
    orderDate: '2025-04-30T17:33:00+09:00',
    productName: '알루미늄 태블릿 거치대 PRO',
    productOption: '스페이스그레이',
    salesAmount: 22_760,
    orderCount: 1,
    externalProductInfo: 'SB-STD-TB',
    receiverName: '정민호',
    receiverPhoneNo: '010-6847-2938',
    receiverAddress: '서울시 서초구 서초대로 654',
    shippingMemo: '-',
    ordererName: '정민호',
    orderStatus: '구매확정',
  }),
  createOrder({
    orderNo: '20241028154',
    market: marketList[0],
    orderDate: '2025-04-21T14:50:00+09:00',
    productName: '프리미엄 무선 이어폰 X9 Pro',
    productOption: '블랙',
    salesAmount: 12_030,
    orderCount: 1,
    externalProductInfo: 'SB-EAR-X9',
    receiverName: '홍길동',
    receiverPhoneNo: '010-1234-5678',
    receiverAddress: '경기도 수원시 영통구 광교중앙로 111',
    shippingMemo: '-',
    ordererName: '홍길동',
    orderStatus: '구매확정',
  }),
  createOrder({
    orderNo: '20241029102',
    market: marketList[0],
    orderDate: '2025-04-22T09:23:00+09:00',
    productName: '스마트 LED 스탠드 조명 S3',
    productOption: '화이트 / 2개입',
    salesAmount: 11_480,
    orderCount: 2,
    externalProductInfo: 'SB-LMP-S3',
    receiverName: '김소희',
    receiverPhoneNo: '010-7384-2948',
    receiverAddress: '부산시 해운대구 센텀동로 222',
    shippingMemo: '-',
    ordererName: '김소희',
    orderStatus: '구매확정',
  }),
  createOrder({
    orderNo: '20241030143',
    market: marketList[0],
    orderDate: '2025-04-23T11:47:00+09:00',
    productName: '접이식 알루미늄 노트북 거치대',
    productOption: '실버 M사이즈',
    salesAmount: 21_120,
    orderCount: 1,
    externalProductInfo: 'SB-STD-NB',
    receiverName: '이준서',
    receiverPhoneNo: '010-8473-9284',
    receiverAddress: '서울시 송파구 올림픽로 333',
    shippingMemo: '-',
    ordererName: '이준서',
    orderStatus: '구매확정',
  }),
  createOrder({
    orderNo: '20241031120',
    market: marketList[0],
    orderDate: '2025-04-24T16:32:00+09:00',
    productName: '고속충전 USB-C 케이블 2m',
    productOption: '화이트',
    salesAmount: 8_700,
    orderCount: 3,
    externalProductInfo: 'SB-CBL-TC',
    receiverName: '박하은',
    receiverPhoneNo: '010-9384-7284',
    receiverAddress: '대구시 수성구 달구벌대로 444',
    shippingMemo: '-',
    ordererName: '박하은',
    orderStatus: '구매확정',
  }),
];

const claimReason = '취소사유가 들어갑니다. 취소사유가 들어갑니다. 취소사유가 들어갑니다.';

const cancelOrders = [
  createOrder({
    orderNo: '20241101153',
    market: marketList[0],
    orderDate: '2025-04-25T13:15:00+09:00',
    productName: '블루투스 5.0 포터블 스피커',
    productOption: '네이비',
    salesAmount: 12_450,
    orderCount: 1,
    externalProductInfo: 'SB-SPK-BT',
    receiverName: '최도현',
    receiverPhoneNo: '010-3847-2918',
    receiverAddress: claimReason,
    shippingMemo: claimReason,
    ordererName: '최도현',
    orderStatus: '취소 승인 요청',
  }),
  createOrder({
    orderNo: '20241104120',
    market: marketList[1],
    orderDate: '2025-04-28T08:56:00+09:00',
    productName: '블루투스 5.0 포터블 스피커',
    productOption: '베이지',
    salesAmount: 11_230,
    orderCount: 1,
    externalProductInfo: 'SB-RST-MF',
    receiverName: '',
    receiverPhoneNo: '',
    receiverAddress: '',
    shippingMemo: claimReason,
    ordererName: '',
    orderStatus: '취소 완료',
  }),
];

const exchangeOrders = [
  createOrder({
    orderNo: '20241105143',
    market: marketList[0],
    orderDate: '2025-04-29T14:21:00+09:00',
    productName: '4구 멀티탭 USB 2포트 1.5m',
    productOption: '화이트 1.5m',
    salesAmount: 7_840,
    orderCount: 2,
    externalProductInfo: 'SB-PWR-4P',
    receiverName: '홍길동',
    receiverPhoneNo: '010-1234-5678',
    receiverAddress: claimReason,
    shippingMemo: claimReason,
    ordererName: '홍길동',
    orderStatus: '교환 승인 요청',
  }),
  createOrder({
    orderNo: '20241029102',
    market: marketList[1],
    orderDate: '2025-04-22T09:23:00+09:00',
    productName: '스마트 LED 스탠드 조명 S3',
    productOption: '화이트 / 2개입',
    salesAmount: 8_120,
    orderCount: 2,
    externalProductInfo: 'SB-LMP-S3',
    receiverName: '',
    receiverPhoneNo: '',
    receiverAddress: '',
    shippingMemo: claimReason,
    ordererName: '',
    orderStatus: '교환 완료',
  }),
];

const returnOrders = [
  createOrder({
    orderNo: '20241030143',
    market: marketList[0],
    orderDate: '2025-04-23T11:47:00+09:00',
    productName: '접이식 알루미늄 노트북 거치대',
    productOption: '실버 M사이즈',
    salesAmount: 13_450,
    orderCount: 1,
    externalProductInfo: 'SB-STD-NB',
    receiverName: '박하은',
    receiverPhoneNo: '',
    receiverAddress: claimReason,
    shippingMemo: claimReason,
    ordererName: '박하은',
    orderStatus: '반품 승인 요청',
  }),
  createOrder({
    orderNo: '20241031120',
    market: marketList[0],
    orderDate: '2025-04-24T16:32:00+09:00',
    productName: '고속충전 USB-C 케이블 2m',
    productOption: '화이트',
    salesAmount: 22_890,
    orderCount: 3,
    externalProductInfo: 'SB-CBL-TC',
    receiverName: '최도현',
    receiverPhoneNo: '',
    receiverAddress: claimReason,
    shippingMemo: claimReason,
    ordererName: '최도현',
    orderStatus: '반품 승인 요청',
  }),
  createOrder({
    orderNo: '20241101153',
    market: marketList[0],
    orderDate: '2025-04-25T13:15:00+09:00',
    productName: '블루투스 5.0 포터블 스피커',
    productOption: '네이비',
    salesAmount: 13_210,
    orderCount: 1,
    externalProductInfo: 'SB-SPK-BT',
    receiverName: '김민준',
    receiverPhoneNo: '',
    receiverAddress: claimReason,
    shippingMemo: claimReason,
    ordererName: '김민준',
    orderStatus: '반품 진행',
  }),
  createOrder({
    orderNo: '202411021047',
    market: marketList[1],
    orderDate: '2025-04-26T10:08:00+09:00',
    productName: '아이폰15 Pro 강화유리 필름',
    productOption: '아이폰15 Pro용',
    salesAmount: 9_050,
    orderCount: 2,
    externalProductInfo: 'SB-FLM-IP15',
    receiverName: '',
    receiverPhoneNo: '',
    receiverAddress: '',
    shippingMemo: claimReason,
    ordererName: '',
    orderStatus: '반품 완료',
  }),
];

const createOrderList = ({
  listOrders,
  totalCount,
  totalPages,
  availableActionCount,
}: {
  listOrders: OrderItem[];
  totalCount: number;
  totalPages: number;
  availableActionCount: number;
}): OrderList => ({
  counts,
  pagination: {
    totalCount,
    totalPages,
    hasNext: true,
    currentPage: 1,
  },
  orders: listOrders,
  availableActionCount,
});

const orderList = createOrderList({
  listOrders: orders,
  totalCount: membershipOrderPreviewCounts.newOrderCount,
  totalPages: 10,
  availableActionCount: 14,
});

const readyToShipOrderList = createOrderList({
  listOrders: readyToShipOrders,
  totalCount: membershipOrderPreviewCounts.shippingReadyCount,
  totalPages: 10,
  availableActionCount: 29,
});

const shippingOrderList = createOrderList({
  listOrders: shippingOrders,
  totalCount: membershipOrderPreviewCounts.shippingCount,
  totalPages: 1,
  availableActionCount: 4,
});

const deliveredOrderList = createOrderList({
  listOrders: deliveredOrders,
  totalCount: membershipOrderPreviewCounts.shippingCompletedCount,
  totalPages: 10,
  availableActionCount: 78,
});

const cancelOrderList = createOrderList({
  listOrders: cancelOrders,
  totalCount: membershipOrderPreviewCounts.cancelRequestCount,
  totalPages: 1,
  availableActionCount: 29,
});

const exchangeOrderList = createOrderList({
  listOrders: exchangeOrders,
  totalCount: membershipOrderPreviewCounts.exchangeRequestCount,
  totalPages: 1,
  availableActionCount: 2,
});

const returnOrderList = createOrderList({
  listOrders: returnOrders,
  totalCount: membershipOrderPreviewCounts.returnRequestCount,
  totalPages: 1,
  availableActionCount: 4,
});

const orderListResponses: Record<
  MembershipOrderDetailsPreviewStatusTab,
  CplatApiResponse<OrderList[]>
> = {
  NewOrder: {
    code: '200',
    message: 'success',
    data: [orderList],
  },
  ShippingReady: {
    code: '200',
    message: 'success',
    data: [readyToShipOrderList],
  },
  Shipping: {
    code: '200',
    message: 'success',
    data: [shippingOrderList],
  },
  ShippingCompleted: {
    code: '200',
    message: 'success',
    data: [deliveredOrderList],
  },
  CancelRequest: {
    code: '200',
    message: 'success',
    data: [cancelOrderList],
  },
  ExchangeRequest: {
    code: '200',
    message: 'success',
    data: [exchangeOrderList],
  },
  ReturnRequest: {
    code: '200',
    message: 'success',
    data: [returnOrderList],
  },
};

const getPreviewSearchTargets = (order: OrderItem, searchField: OrderSeachField) => {
  switch (searchField) {
    case 'ExternalProductInfo':
      return [order.externalProductInfo, order.productNo, order.orderNo, order.orderItemNo];
    case 'ProductName':
      return [order.productName, order.productOption];
    case 'OrdererName':
      return [order.ordererInfo.ordererName, order.ordererInfo.ordererPhoneNo];
    case 'ReceiverName':
      return [
        order.receiverInfo.receiverName,
        order.receiverInfo.receiverPhoneNo,
        order.receiverInfo.receiverAddress,
      ];
    default:
      return [];
  }
};

const matchesPreviewSearch = (order: OrderItem, filters: MembershipOrderDetailsPreviewFilters) => {
  const keyword = filters.searchKeyword?.trim().toLowerCase();
  if (!filters.searchField || !keyword) return true;

  return getPreviewSearchTargets(order, filters.searchField).some((target) =>
    target.toLowerCase().includes(keyword),
  );
};

const getPreviewSortValue = (
  order: OrderItem,
  sortColumn: MembershipOrderDetailsPreviewSortInfo['sortColumn'],
) => {
  switch (sortColumn) {
    case 'OrderDate':
      return new Date(order.orderDate).getTime();
    case 'SalesAmount':
      return order.salesAmount;
    case 'OrderCount':
      return order.orderCount;
    default:
      return 0;
  }
};

const sortPreviewOrders = (
  orders: OrderItem[],
  sortInfo?: MembershipOrderDetailsPreviewSortInfo,
) => {
  if (!sortInfo) return orders;

  const direction = sortInfo.sortDirection === 'asc' ? 1 : -1;
  return [...orders].sort((firstOrder, secondOrder) => {
    const firstValue = getPreviewSortValue(firstOrder, sortInfo.sortColumn);
    const secondValue = getPreviewSortValue(secondOrder, sortInfo.sortColumn);
    return (firstValue - secondValue) * direction;
  });
};

const filterPreviewOrders = (
  orders: OrderItem[],
  filters: MembershipOrderDetailsPreviewFilters = {},
) => {
  const dateFromKey = filters.dateFrom?.slice(0, 10);
  const dateToKey = filters.dateTo?.slice(0, 10);

  const filteredOrders = orders.filter((order) => {
    const orderDateKey = order.orderDate.slice(0, 10);

    if (dateFromKey && orderDateKey < dateFromKey) return false;
    if (dateToKey && orderDateKey > dateToKey) return false;
    if (filters.storeId && order.storeId !== filters.storeId) return false;
    if (filters.marketId && order.marketId !== filters.marketId) return false;
    if (filters.isOrderFurtherActionEnable && !order.marketInfo.isOrderFurtherActionEnable) {
      return false;
    }

    return matchesPreviewSearch(order, filters);
  });

  return sortPreviewOrders(filteredOrders, filters.sortInfo);
};

const createPreviewOrderListResponse = (
  statusTab: MembershipOrderDetailsPreviewStatusTab,
  filters?: MembershipOrderDetailsPreviewFilters,
): CplatApiResponse<OrderList[]> => {
  const baseOrderList = orderListResponses[statusTab].data[0];
  const dateAdjustedOrders = remapPreviewOrderDates(baseOrderList.orders, filters);
  const filteredOrders = filterPreviewOrders(dateAdjustedOrders, filters);
  const skip = filters?.skip ?? 0;
  const take = filters?.take ?? filteredOrders.length;
  const pagedOrders = take > 0 ? filteredOrders.slice(skip, skip + take) : filteredOrders;
  const totalPages = take > 0 ? Math.ceil(filteredOrders.length / take) : 1;

  return {
    code: '200',
    message: 'success',
    data: [
      {
        ...baseOrderList,
        pagination: {
          ...baseOrderList.pagination,
          totalCount: filteredOrders.length,
          totalPages: Math.max(1, totalPages),
          hasNext: take > 0 ? skip + take < filteredOrders.length : false,
          currentPage: take > 0 ? Math.floor(skip / take) + 1 : 1,
        },
        orders: pagedOrders,
        availableActionCount: filteredOrders.filter(
          (order) => order.marketInfo.isOrderFurtherActionEnable,
        ).length,
      },
    ],
  };
};

export const fetchMembershipOrderDetailsPreviewOrderList = async (
  statusTab: MembershipOrderDetailsPreviewStatusTab,
  filters?: MembershipOrderDetailsPreviewFilters,
) => createPreviewOrderListResponse(statusTab, filters);

const isOrderStatusTab = (
  statusTab: MembershipOrderDetailsPreviewStatusTab,
): statusTab is MembershipOrderDetailsPreviewOrderStatusTab =>
  statusTab === 'NewOrder' ||
  statusTab === 'ShippingReady' ||
  statusTab === 'Shipping' ||
  statusTab === 'ShippingCompleted';

const createCommonPreviewQueryParams = (filters?: MembershipOrderDetailsPreviewFilters) => ({
  cplatToken: 'membership-preview',
  dateFrom: filters?.dateFrom ?? '',
  dateTo: filters?.dateTo ?? '',
  skip: filters?.skip ?? 0,
  take: filters?.take ?? 0,
  ...(filters?.storeId && { storeId: filters.storeId }),
  ...(filters?.marketId !== undefined && { marketId: filters.marketId }),
  ...(filters?.isOrderFurtherActionEnable && {
    isOrderFurtherActionEnable: filters.isOrderFurtherActionEnable,
  }),
  ...(filters?.sortInfo && {
    sortInfo: {
      sortColumn: filters.sortInfo.sortColumn,
      sortDirection: filters.sortInfo.sortDirection,
    },
  }),
  ...(filters?.searchField &&
    filters.searchKeyword && {
      searchField: filters.searchField,
      searchKeyword: filters.searchKeyword,
    }),
});

const createPreviewOrderQueryParams = (
  statusTab: MembershipOrderDetailsPreviewOrderStatusTab,
  filters?: MembershipOrderDetailsPreviewFilters,
): Parameters<typeof orderQueries.getOrderList>[0] => ({
  ...createCommonPreviewQueryParams(filters),
  statusTab,
});

const createPreviewOrderChangeQueryParams = (
  statusTab: MembershipOrderDetailsPreviewOrderChangeStatusTab,
  filters?: MembershipOrderDetailsPreviewFilters,
): Parameters<typeof orderQueries.getOrderChangeList>[0] => ({
  ...createCommonPreviewQueryParams(filters),
  statusTab,
});

export const membershipOrderDetailsMock = {
  startDate: previewStartDate,
  endDate: previewEndDate,
  lastUpdatedLabel: '업데이트 14:53',
  collectButtonLabel: '주문수집',
  marketList,
  registeredStores,
  shippingCompanyList,
  readyToShipCarrierState,
  orderList,
  readyToShipOrderList,
  shippingOrderList,
  deliveredOrderList,
  cancelOrderList,
  exchangeOrderList,
  returnOrderList,
  orderListResponses,
  orderListResponse: {
    code: '200',
    message: 'success',
    data: [orderList],
  } satisfies CplatApiResponse<OrderList[]>,
};

export function membershipOrderDetailsPreviewOrderQuery(
  scope: string,
  statusTab?: MembershipOrderDetailsPreviewOrderStatusTab,
  filters?: MembershipOrderDetailsPreviewFilters,
): ReturnType<typeof orderQueries.getOrderList>;
export function membershipOrderDetailsPreviewOrderQuery(
  scope: string,
  statusTab: MembershipOrderDetailsPreviewOrderChangeStatusTab,
  filters?: MembershipOrderDetailsPreviewFilters,
): ReturnType<typeof orderQueries.getOrderChangeList>;
export function membershipOrderDetailsPreviewOrderQuery(
  _scope: string,
  statusTab: MembershipOrderDetailsPreviewStatusTab = 'NewOrder',
  filters?: MembershipOrderDetailsPreviewFilters,
):
  | ReturnType<typeof orderQueries.getOrderList>
  | ReturnType<typeof orderQueries.getOrderChangeList> {
  if (isOrderStatusTab(statusTab)) {
    const query = orderQueries.getOrderList(createPreviewOrderQueryParams(statusTab, filters));

    return {
      ...query,
      queryFn: async () => createPreviewOrderListResponse(statusTab, filters),
      staleTime: Infinity,
    };
  }

  const query = orderQueries.getOrderChangeList(
    createPreviewOrderChangeQueryParams(statusTab, filters),
  );

  return {
    ...query,
    queryFn: async () => createPreviewOrderListResponse(statusTab, filters),
    staleTime: Infinity,
  };
}
